/* =====================================================================
 * Official Skill Store — Owner가 관리하는 공식 스킬 전역 상태
 *
 * SkillCreatePage (owner 관리)와 SkillModal ReferenceTab (모든 사용자)에서 공용.
 * ===================================================================== */

import { create } from 'zustand';
import type { OfficialSkillSummary, OutputFormat } from '@auw/api-client';
import {
  listOfficialSkills,
  listAllOfficialSkills,
  createOfficialSkill as createApi,
  updateOfficialSkill as updateApi,
  deleteOfficialSkill as deleteApi,
  updateOfficialSkillStatus as statusApi,
  getOfficialSkillContent as contentApi,
} from '@auw/api-client';

interface OfficialSkillState {
  skills: OfficialSkillSummary[];
  isLoading: boolean;
  error: string | null;

  fetchPublicSkills: (category?: string) => Promise<void>;
  fetchAllSkills: () => Promise<void>;
  createSkill: (data: {
    name: string;
    description: string;
    category: string;
    format: 'md' | 'zip';
    output_format?: OutputFormat;
    content: string;
    status?: 'draft' | 'active';
  }) => Promise<OfficialSkillSummary>;
  updateSkill: (
    skillId: string,
    data: Partial<{
      name: string;
      description: string;
      category: string;
      output_format: OutputFormat;
      content: string;
      status: 'draft' | 'active';
    }>,
  ) => Promise<OfficialSkillSummary>;
  deleteSkill: (skillId: string) => Promise<void>;
  toggleStatus: (skillId: string) => Promise<void>;
  getContent: (skillId: string) => Promise<string>;
}

export const useOfficialSkillStore = create<OfficialSkillState>((set, get) => ({
  skills: [],
  isLoading: false,
  error: null,

  fetchPublicSkills: async (category?: string) => {
    set({ isLoading: true, error: null });
    try {
      const res = await listOfficialSkills(category);
      set({ skills: res.skills, isLoading: false });
    } catch (err) {
      set({ error: (err as Error).message, isLoading: false });
    }
  },

  fetchAllSkills: async () => {
    set({ isLoading: true, error: null });
    try {
      const res = await listAllOfficialSkills();
      set({ skills: res.skills, isLoading: false });
    } catch (err) {
      set({ error: (err as Error).message, isLoading: false });
    }
  },

  createSkill: async (data) => {
    const result = await createApi(data);
    set((s) => ({ skills: [result, ...s.skills] }));
    return result;
  },

  updateSkill: async (skillId, data) => {
    const result = await updateApi(skillId, data);
    set((s) => ({
      skills: s.skills.map((sk) => (sk.skill_id === skillId ? result : sk)),
    }));
    return result;
  },

  deleteSkill: async (skillId) => {
    await deleteApi(skillId);
    set((s) => ({
      skills: s.skills.filter((sk) => sk.skill_id !== skillId),
    }));
  },

  toggleStatus: async (skillId) => {
    const skill = get().skills.find((s) => s.skill_id === skillId);
    if (!skill) return;
    const newStatus = skill.status === 'active' ? 'draft' : 'active';
    await statusApi(skillId, newStatus);
    set((s) => ({
      skills: s.skills.map((sk) =>
        sk.skill_id === skillId ? { ...sk, status: newStatus } : sk,
      ),
    }));
  },

  getContent: async (skillId) => {
    return contentApi(skillId);
  },
}));
